import { useEffect, useRef, useState } from "react";
import {
  Alert,
  AlertTitle,
  Box,
  Checkbox,
  DialogContent,
  Divider,
  FormControl,
  FormControlLabel,
  FormHelperText,
  FormLabel,
  Link,
  MenuItem,
  RadioGroup,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import type { Promisable } from "type-fest";

import {
  InvolveGuestAsSharedAssessmentReaderRequestSchema,
  professionalRelations,
  type InvolveGuestAsSharedAssessmentReaderRequest,
  type ProfessionalRelation,
} from "@thalamos/haven-common";

import { shortCalendarDayText } from "../../shortCalendarDayText.js";
import {
  ChoiceTile,
  ChosenRow,
  countTextStyle,
  gutter,
  shareColumns,
  ShareStepActions,
  StackedCells,
  useStackedLayout,
  type ShareableAssessment,
} from "./share_step_parts.js";

/** How many chosen assessments the summary shows before Show all. */
const namedAtFirst = 3;

const mediumDate = new Intl.DateTimeFormat("en-GB", { dateStyle: "medium" });

/** What the sharer has written of who they share with and for how long. */
export type WhoDraft = {
  name: string;
  email: string;
  relation: ProfessionalRelation | "";
  relationDescription: string;
  jobTitle: string;
  endChoice: "default" | "chosen";
  /** The day access ends on where one is chosen, as the date field gives it. */
  chosenDay: string;
  confirmedSafe: boolean;
};

export const emptyWhoDraft: WhoDraft = {
  name: "",
  email: "",
  relation: "",
  relationDescription: "",
  jobTitle: "",
  endChoice: "default",
  chosenDay: "",
  confirmedSafe: false,
};

/** Why the last share failed, where the step shows it beneath the form. */
export type ShareFailure = "already_shared" | "no_date_of_birth" | "failed";

/** What pressing Share found: a request to send, or a form not yet complete. */
export type ShareAttempt =
  | { kind: "complete"; request: InvolveGuestAsSharedAssessmentReaderRequest }
  | { kind: "incomplete" };

type Field = "name" | "email" | "relationDescription" | "jobTitle" | "endsOn";

/**
 * The second step of sharing: who the guest is and the day their access
 * ends, beneath a summary of what is shared. Pressing Share with the form
 * incomplete brings its first problem into view.
 */
export const ShareWhoStep = ({
  personName,
  chosen,
  defaultEndsOn,
  draft,
  onDraftChange,
  showProblems,
  addressRefusal,
  failure,
  sending,
  onChangeChosen,
  onCancel,
  onShare,
}: {
  /** Who the assessments are about, as the step names them. */
  personName: string;
  /** The chosen assessments, newest first. */
  chosen: readonly ShareableAssessment[];
  /** The calendar day access ends on unless another is chosen. */
  defaultEndsOn: string;
  draft: WhoDraft;
  onDraftChange: (draft: WhoDraft) => Promisable<void>;
  /** Whether Share has been pressed, so the form says what it lacks. */
  showProblems: boolean;
  /** The server's words for an address it refused. */
  addressRefusal?: string | undefined;
  failure?: ShareFailure | undefined;
  sending: boolean;
  onChangeChosen: () => Promisable<void>;
  onCancel: () => Promisable<void>;
  onShare: (attempt: ShareAttempt) => Promisable<void>;
}) => {
  const { t } = useTranslation();
  const { ref, stacked } = useStackedLayout();
  const [showingAllChosen, setShowingAllChosen] = useState(false);
  // Counts sends refused for what the form says, so each one brings the first
  // problem into view, once its message has rendered.
  const [refusedSends, setRefusedSends] = useState(0);
  const contentRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (refusedSends === 0) {
      return;
    }
    const firstError = contentRef.current?.querySelector(".Mui-error");
    const field = firstError?.closest(".MuiFormControl-root") ?? firstError;
    field?.scrollIntoView({ block: "center", behavior: "smooth" });
    field
      ?.querySelector<HTMLElement>("input:not([type=hidden]), [role=combobox]")
      ?.focus({ preventScroll: true });
  }, [refusedSends]);

  const edit = (change: Partial<WhoDraft>) => {
    void onDraftChange({ ...draft, ...change });
  };

  const parsed = InvolveGuestAsSharedAssessmentReaderRequestSchema.safeParse({
    assessmentIds: chosen.map(({ assessment }) => assessment.id),
    name: draft.name,
    email: draft.email.trim(),
    earlierGuestUserId: null,
    relation: {
      relationGroup: "professional",
      relation: draft.relation,
      relationDescription:
        draft.relation === "other" ? draft.relationDescription : null,
    },
    jobTitle: draft.jobTitle,
    endsOn: draft.endChoice === "default" ? defaultEndsOn : draft.chosenDay,
  });

  // The schema's messages are translation keys; an address it refuses says
  // so in words of the step's own.
  const problemWith = (field: Field): string | undefined => {
    const issue = parsed.error?.issues.find((each) =>
      field === "relationDescription"
        ? each.path[0] === "relation" && each.path[1] === field
        : each.path[0] === field,
    );

    if (!showProblems || issue === undefined) {
      return undefined;
    }

    return field === "email"
      ? t("askGuest.emailInvalid")
      : t(issue.message, { defaultValue: t("askGuest.invalid") });
  };
  const relationMissing = showProblems && draft.relation === "";
  const confirmationMissing = showProblems && !draft.confirmedSafe;

  const oldest = chosen.at(-1)?.assessment.finishedAt;
  const newest = chosen[0]?.assessment.finishedAt;
  const range =
    oldest === undefined || newest === undefined
      ? ""
      : mediumDate.format(oldest) === mediumDate.format(newest)
        ? t("shareWithGuest.who.onOneDay", { date: mediumDate.format(newest) })
        : t("shareWithGuest.who.range", {
            from: mediumDate.format(oldest),
            to: mediumDate.format(newest),
          });
  const named = showingAllChosen ? chosen : chosen.slice(0, namedAtFirst);

  return (
    <>
      <DialogContent ref={contentRef} dividers sx={{ px: gutter }}>
        <Stack ref={ref} spacing={2.5}>
          <Box
            sx={{
              border: 1,
              borderColor: "divider",
              borderRadius: 1,
              overflow: "hidden",
            }}
          >
            <Stack
              direction="row"
              spacing={2}
              useFlexGap
              alignItems="baseline"
              flexWrap="wrap"
              sx={{
                px: 2,
                py: 1.25,
                bgcolor: "grey.50",
                borderBottom: 1,
                borderColor: "divider",
              }}
            >
              <Typography variant="subtitle2" component="h3">
                {t("shareWithGuest.who.sharing", { count: chosen.length })}
                {chosen.length > named.length ? (
                  <Typography
                    component="span"
                    variant="body2"
                    color="text.secondary"
                  >
                    {` · ${range}`}
                  </Typography>
                ) : null}
              </Typography>
              <Link
                component="button"
                type="button"
                variant="body2"
                underline="hover"
                disabled={sending}
                onClick={() => {
                  void onChangeChosen();
                }}
              >
                {t("shareWithGuest.who.change")}
              </Link>
            </Stack>
            {stacked ? (
              <Stack divider={<Divider />}>
                {named.map((each) => (
                  <Box key={each.assessment.id} sx={{ px: 2, py: 1.5 }}>
                    <StackedCells assessment={each} />
                  </Box>
                ))}
              </Stack>
            ) : (
              <Table size="small">
                <TableHead>
                  <TableRow>
                    {shareColumns.map((column) => (
                      <TableCell key={column}>
                        {t(`personPage.assessments.history.column.${column}`)}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {named.map((each) => (
                    <ChosenRow key={each.assessment.id} assessment={each} />
                  ))}
                </TableBody>
              </Table>
            )}
            {chosen.length > namedAtFirst ? (
              <Stack
                direction="row"
                spacing={2}
                alignItems="baseline"
                sx={{ px: 2, py: 1.25, borderTop: 1, borderColor: "divider" }}
              >
                <Typography variant="body2" sx={countTextStyle}>
                  {t("shareWithGuest.choose.shown", {
                    shown: named.length,
                    total: chosen.length,
                  })}
                </Typography>
                {showingAllChosen ? null : (
                  <Link
                    component="button"
                    type="button"
                    variant="body2"
                    underline="hover"
                    onClick={() => {
                      setShowingAllChosen(true);
                    }}
                  >
                    {t("shareWithGuest.who.showAll")}
                  </Link>
                )}
              </Stack>
            ) : null}
          </Box>
          <Stack direction={stacked ? "column" : "row"} spacing={2}>
            <TextField
              fullWidth
              label={t("askGuest.name")}
              value={draft.name}
              disabled={sending}
              error={problemWith("name") !== undefined}
              helperText={problemWith("name")}
              onChange={(event) => {
                edit({ name: event.target.value });
              }}
            />
            <TextField
              fullWidth
              label={t("askGuest.email")}
              type="email"
              value={draft.email}
              disabled={sending}
              error={
                problemWith("email") !== undefined ||
                addressRefusal !== undefined
              }
              helperText={
                problemWith("email") ??
                addressRefusal ??
                t("shareWithGuest.who.emailHelp")
              }
              onChange={(event) => {
                edit({ email: event.target.value });
              }}
            />
          </Stack>
          <TextField
            select
            label={t("askGuest.relation")}
            value={draft.relation}
            disabled={sending}
            error={relationMissing}
            helperText={
              relationMissing ? t("askGuest.relationRequired") : undefined
            }
            onChange={(event) => {
              const picked = professionalRelations.find(
                (each) => each === event.target.value,
              );
              edit({ relation: picked ?? "" });
            }}
          >
            {/* Guests are professionals reached at a work address, so the
                family and carer groups are not offered. */}
            {professionalRelations.map((each) => (
              <MenuItem key={each} value={each}>
                {t(`standing.relation.professional.${each}`)}
              </MenuItem>
            ))}
          </TextField>
          {draft.relation === "other" ? (
            <TextField
              label={t("askGuest.relationDescription")}
              value={draft.relationDescription}
              disabled={sending}
              error={problemWith("relationDescription") !== undefined}
              helperText={problemWith("relationDescription")}
              onChange={(event) => {
                edit({ relationDescription: event.target.value });
              }}
            />
          ) : null}
          <TextField
            label={t("askGuest.jobTitle")}
            value={draft.jobTitle}
            disabled={sending}
            error={problemWith("jobTitle") !== undefined}
            helperText={problemWith("jobTitle")}
            onChange={(event) => {
              edit({ jobTitle: event.target.value });
            }}
          />
          <FormControl>
            <FormLabel
              id="share-guest-ends"
              sx={{ mb: 1, typography: "subtitle2", color: "text.primary" }}
            >
              {t("shareWithGuest.who.endsOn")}
            </FormLabel>
            <RadioGroup
              aria-labelledby="share-guest-ends"
              row={!stacked}
              value={draft.endChoice}
              onChange={(event) => {
                edit({
                  endChoice:
                    event.target.value === "chosen" ? "chosen" : "default",
                });
              }}
              sx={{ gap: 1 }}
            >
              <ChoiceTile
                value="default"
                picked={draft.endChoice === "default"}
                disabled={sending}
                label={t("shareWithGuest.who.endsAfterThreeMonths", {
                  date: shortCalendarDayText(defaultEndsOn),
                })}
              />
              <ChoiceTile
                value="chosen"
                picked={draft.endChoice === "chosen"}
                disabled={sending}
                label={t("shareWithGuest.who.endsOnChosenDay")}
              />
            </RadioGroup>
          </FormControl>
          {draft.endChoice === "chosen" ? (
            <TextField
              type="date"
              label={t("askGuest.chosenDay")}
              value={draft.chosenDay}
              disabled={sending}
              error={problemWith("endsOn") !== undefined}
              helperText={
                problemWith("endsOn") ?? t("shareWithGuest.who.chosenDayHelp")
              }
              onChange={(event) => {
                edit({ chosenDay: event.target.value });
              }}
              slotProps={{ inputLabel: { shrink: true } }}
              sx={{ maxWidth: 240 }}
            />
          ) : null}
          <Alert severity="warning" icon={false}>
            <AlertTitle>{t("shareWithGuest.who.warningTitle")}</AlertTitle>
            <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
              {(["whole", "email", "relationship"] as const).map((each) => (
                <Typography key={each} component="li" variant="body2">
                  {t(`shareWithGuest.who.warningPoint.${each}`, {
                    count: chosen.length,
                    personName,
                  })}
                </Typography>
              ))}
            </Box>
            <FormControl error={confirmationMissing} sx={{ mt: 1 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={draft.confirmedSafe}
                    disabled={sending}
                    onChange={(event) => {
                      edit({ confirmedSafe: event.target.checked });
                    }}
                  />
                }
                label={t("shareWithGuest.who.confirmSafe", {
                  count: chosen.length,
                })}
                slotProps={{ typography: { sx: { fontWeight: 600 } } }}
              />
              {confirmationMissing ? (
                <FormHelperText>
                  {t("shareWithGuest.who.confirmSafeRequired")}
                </FormHelperText>
              ) : null}
            </FormControl>
          </Alert>
          {failure === undefined ? null : (
            <Alert severity="error">
              {t(`shareWithGuest.outcome.${failure}`, { personName })}
            </Alert>
          )}
        </Stack>
      </DialogContent>
      <ShareStepActions
        stacked={stacked}
        sending={sending}
        proceedLabel={t("shareWithGuest.who.send")}
        onCancel={onCancel}
        onProceed={() => {
          if (parsed.success && draft.confirmedSafe) {
            void onShare({ kind: "complete", request: parsed.data });
          } else {
            setRefusedSends((count) => count + 1);
            void onShare({ kind: "incomplete" });
          }
        }}
      />
    </>
  );
};
